import React, { useEffect, useState } from 'react';
import { RefreshCw, X, Sparkles } from 'lucide-react';
import { LEAGUE_DATA_UPDATED_EVENT, type LeagueDataUpdatedDetail } from '../utils/leagueDataEvents';

/**
 * Aviso de "cambió la información de tu liga" (v6.11.43).
 *
 * Hermano de `AppUpdateBanner`: aquel avisa que hay CÓDIGO nuevo (service worker),
 * este avisa que hay DATOS nuevos. Lo dispara AuthProvider/App cuando un refresco
 * en segundo plano detecta un cambio estructural — zona asignada, categoría,
 * edición, o el arranque de la temporada.
 *
 * Los datos YA se aplicaron en caliente cuando aparece este cartel (por eso el
 * copy dice "ya lo actualizamos"). El botón "Actualizar" es la red de seguridad
 * para lo que igual pudiera haber quedado viejo en pantalla, y responde al pedido
 * concreto: tener una forma de enterarse sin descubrirlo recargando a mano.
 *
 * Se ubica arriba para no chocar con `AppUpdateBanner` (abajo) ni con la bottom nav.
 */
export const LeagueDataUpdateBanner: React.FC = () => {
  const [detail, setDetail] = useState<LeagueDataUpdatedDetail | null>(null);

  useEffect(() => {
    const onUpdate = (event: Event) => {
      const next = (event as CustomEvent<LeagueDataUpdatedDetail>).detail;
      if (next?.message) setDetail(next);
    };
    window.addEventListener(LEAGUE_DATA_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(LEAGUE_DATA_UPDATED_EVENT, onUpdate);
  }, []);

  if (!detail) return null;

  return (
    <div className="fixed inset-x-0 top-3 z-[60] flex justify-center px-4 animate-slide-up pointer-events-none">
      <div className="flex items-start gap-3 bg-slate-900 text-white rounded-2xl shadow-[var(--shadow-float)] px-4 py-3 max-w-sm w-full pointer-events-auto border border-white/10">
        <Sparkles size={18} className="text-[var(--color-secondary)] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold leading-tight">{detail.message}</p>
          <p className="text-[11px] text-white/60 leading-tight mt-0.5">
            Ya lo actualizamos en la app.
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          aria-label="Recargar la app"
          title="Recargar"
          className="bg-[var(--color-secondary)] text-[var(--color-primary)] rounded-lg p-2 active:scale-95 transition-transform shrink-0"
        >
          <RefreshCw size={14} />
        </button>
        <button
          onClick={() => setDetail(null)}
          aria-label="Cerrar aviso"
          className="text-white/50 hover:text-white shrink-0 transition-colors mt-1"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
